import React, { useState, useEffect } from "react";
import {
  List,
  EditButton,
  DeleteButton,
  CreateButton,
  TopToolbar,
  useListContext,
  useNotify,
} from "react-admin";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Stack,
  IconButton,
  Tooltip,
  Chip,
} from "@mui/material";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { penaltyTypeApi } from "../dataProvider/resources/penalties";
import { useClub } from "../club/ClubContext";

type PenaltyTypeItem = {
  id: number;
  name: string;
  description?: string;
  amount: number;
  min_value?: number | null;
  max_value?: number | null;
  delta?: number | null;
  active: boolean;
  sort_order: number;
};

function formatEuro(v: number) {
  return `${Number(v).toFixed(2).replace(".", ",")} €`;
}

function SortButtons({
  items,
  index,
  onMove,
}: {
  items: PenaltyTypeItem[];
  index: number;
  onMove: (from: number, to: number) => void;
}) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      <Tooltip title="Nach oben">
        <span>
          <IconButton
            size="small"
            disabled={index === 0}
            onClick={(e) => {
              e.stopPropagation();
              onMove(index, index - 1);
            }}
          >
            <ArrowUpwardIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Nach unten">
        <span>
          <IconButton
            size="small"
            disabled={index === items.length - 1}
            onClick={(e) => {
              e.stopPropagation();
              onMove(index, index + 1);
            }}
          >
            <ArrowDownwardIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
}

function SortableTypeList() {
  const { data, isPending } = useListContext<PenaltyTypeItem>();
  const notify = useNotify();
  const { clubId } = useClub();
  const [items, setItems] = useState<PenaltyTypeItem[]>([]);

  useEffect(() => {
    if (data) {
      setItems([...data].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)));
    }
  }, [data]);

  if (isPending || !items.length) return null;

  const handleMove = async (from: number, to: number) => {
    if (to < 0 || to >= items.length) return;
    const reordered = [...items];
    [reordered[from], reordered[to]] = [reordered[to], reordered[from]];
    setItems(reordered);
    try {
      await penaltyTypeApi.reorder(
        clubId!,
        reordered.map((item, i) => ({ id: item.id, sort_order: i }))
      );
    } catch {
      setItems(items);
      notify("Fehler beim Speichern der Reihenfolge", { type: "error" });
    }
  };

  return (
    <Box>
      {items.map((item, index) => (
        <Card key={item.id} sx={{ mb: 1 }}>
          <CardContent
            sx={{ display: "flex", gap: 2, alignItems: "center", pb: "12px !important" }}
          >
            <SortButtons items={items} index={index} onMove={handleMove} />
            <Box flex={1} minWidth={0}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Typography fontWeight={600}>{item.name}</Typography>
                {item.min_value != null && item.max_value != null && item.delta != null ? (
                  <Typography fontWeight={700} color="primary.main">
                    {formatEuro(item.min_value)} – {formatEuro(item.max_value)}{" "}
                    <Typography component="span" variant="body2" color="text.secondary">
                      (Schritt {formatEuro(item.delta)})
                    </Typography>
                  </Typography>
                ) : (
                  <Typography fontWeight={700} color="primary.main">
                    {formatEuro(item.amount)}
                  </Typography>
                )}
                {!item.active && (
                  <Chip label="Inaktiv" size="small" color="warning" />
                )}
              </Stack>
              {item.description && (
                <Typography variant="body2" color="text.secondary" noWrap>
                  {item.description}
                </Typography>
              )}
            </Box>
            <Stack direction="row" spacing={0.5} flexShrink={0}>
              <EditButton record={item} label="" />
              <DeleteButton record={item} label="" />
            </Stack>
          </CardContent>
        </Card>
      ))}
    </Box>
  );
}

function PenaltyTypeActions() {
  return (
    <TopToolbar>
      <CreateButton />
    </TopToolbar>
  );
}

export function PenaltyTypeList() {
  return (
    <List
      title="Strafenkatalog"
      actions={<PenaltyTypeActions />}
      perPage={500}
      sort={{ field: "sort_order", order: "ASC" }}
    >
      <SortableTypeList />
    </List>
  );
}
